'use client';

import Link from 'next/link';
import LandingNav from '@/app/public-landing-page/components/LandingNav';
import LandingFooter from '@/app/public-landing-page/components/LandingFooter';
import { useTranslation } from '@/i18n/useTranslation';
import {
  Award,
  Archive,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  ClipboardCheck,
  FileCheck,
  Globe,
  GraduationCap,
  History,
  Landmark,
  LayoutTemplate,
  MessageSquareText,
  ShieldCheck,
  Users,
} from 'lucide-react';

const capabilities = [
  {
    icon: ShieldCheck,
    titleKey: 'for_institutions.cap_program_verification',
    title: 'Program verification',
    descKey: 'for_institutions.cap_program_verification_desc',
    desc: 'Confirm that a program, course, membership, or activity your institution runs is real, active, and tied to your organization.',
    color: 'bg-blue-50 text-blue-700',
  },
  {
    icon: LayoutTemplate,
    titleKey: 'for_institutions.cap_record_templates',
    title: 'Record templates',
    descKey: 'for_institutions.cap_record_templates_desc',
    desc: 'Reusable templates for the kinds of records your institution issues most often, so every confirmation follows the same structure.',
    color: 'bg-blue-50 text-blue-700',
  },
  {
    icon: FileCheck,
    titleKey: 'for_institutions.cap_issued_records',
    title: 'Issued records',
    descKey: 'for_institutions.cap_issued_records_desc',
    desc: 'A running list of every record your institution has confirmed, searchable by person, program, and date issued.',
    color: 'bg-teal-50 text-teal-700',
  },
  {
    icon: Award,
    titleKey: 'for_institutions.cap_verified_achievements',
    title: 'Verified achievements',
    descKey: 'for_institutions.cap_verified_achievements_desc',
    desc: "Recognize completions, awards, and milestones with a confirmation that carries your institution's name.",
    color: 'bg-amber-50 text-amber-700',
  },
  {
    icon: Globe,
    titleKey: 'for_institutions.cap_public_trust_pages',
    title: 'Public trust pages',
    descKey: 'for_institutions.cap_public_trust_pages_desc',
    desc: 'A shareable page for each confirmed record that anyone can view to see what your institution verified, and when.',
    color: 'bg-rose-50 text-rose-700',
  },
  {
    icon: Users,
    titleKey: 'for_institutions.cap_witness_context',
    title: 'Witness context',
    descKey: 'for_institutions.cap_witness_context_desc',
    desc: "Let staff, instructors, or program leads add context to a record alongside the institution's own confirmation.",
    color: 'bg-emerald-50 text-emerald-700',
  },
  {
    icon: History,
    titleKey: 'for_institutions.cap_audit_trail',
    title: 'Audit trail',
    descKey: 'for_institutions.cap_audit_trail_desc',
    desc: 'A clear history of who issued each record, when it changed, and what it looked like at every step.',
    color: 'bg-slate-100 text-slate-700',
  },
  {
    icon: Archive,
    titleKey: 'for_institutions.cap_heritage_archive',
    title: 'Heritage archive',
    descKey: 'for_institutions.cap_heritage_archive_desc',
    desc: 'Curate private or public collections of stories, photographs, milestones, oral histories, and organizational memory.',
    color: 'bg-orange-50 text-orange-800',
  },
];

const useCases = [
  {
    icon: GraduationCap,
    titleKey: 'for_institutions.use_schools',
    title: 'Education',
    descKey: 'for_institutions.use_schools_desc',
    desc: 'Recognize awards, projects, fellowships, program participation, and community contribution.',
  },
  {
    icon: Users,
    titleKey: 'for_institutions.use_clubs',
    title: 'Associations and communities',
    descKey: 'for_institutions.use_clubs_desc',
    desc: 'Document membership, volunteer service, training, awards, and community contribution.',
  },
  {
    icon: BriefcaseBusiness,
    titleKey: 'for_institutions.use_companies',
    title: 'Employers',
    descKey: 'for_institutions.use_companies_desc',
    desc: 'Recognize career milestones, internal programs, service, project achievements, and employee legacy stories.',
  },
  {
    icon: ClipboardCheck,
    titleKey: 'for_institutions.use_nonprofits',
    title: 'Nonprofits',
    descKey: 'for_institutions.use_nonprofits_desc',
    desc: 'Issue records for volunteer work, training programs, and recognized contributions to your mission.',
  },
  {
    icon: Landmark,
    titleKey: 'for_institutions.use_archives',
    title: 'Archives and museums',
    descKey: 'for_institutions.use_archives_desc',
    desc: 'Curate oral histories, photographs, community memory, cultural milestones, and heritage records.',
  },
  {
    icon: BookOpen,
    titleKey: 'for_institutions.use_public_programs',
    title: 'Public programs',
    descKey: 'for_institutions.use_public_programs_desc',
    desc: 'Document participation, service, contribution, awards, and reviewed program outcomes.',
  },
];

const steps = [
  {
    step: '01',
    titleKey: 'for_institutions.step1_title',
    title: 'Define a program or record type',
    descKey: 'for_institutions.step1_desc',
    desc: 'Set the purpose, policy, visibility, evidence expectations, and responsible team roles.',
  },
  {
    step: '02',
    titleKey: 'for_institutions.step2_title',
    title: 'Add or invite participants',
    descKey: 'for_institutions.step2_desc',
    desc: 'Connect the people whose participation, contribution, or achievement the program may recognize.',
  },
  {
    step: '03',
    titleKey: 'for_institutions.step3_title',
    title: 'Collect a submission or evidence',
    descKey: 'for_institutions.step3_desc',
    desc: 'Receive a participant submission or create an institution-led record with supporting context.',
  },
  {
    step: '04',
    titleKey: 'for_institutions.step4_title',
    title: 'Review the record',
    descKey: 'for_institutions.step4_desc',
    desc: 'An authorized reviewer checks the submission, requests changes when needed, and records a decision.',
  },
  {
    step: '05',
    titleKey: 'for_institutions.step5_title',
    title: 'Add attestation or witness context',
    descKey: 'for_institutions.step5_desc',
    desc: "Add the institution's attestation and optional witness or reviewer context when policy permits.",
  },
  {
    step: '06',
    titleKey: 'for_institutions.step6_title',
    title: 'Issue the record',
    descKey: 'for_institutions.step6_desc',
    desc: 'Issue an evidence-backed record with a unique reference, current status, and accountable source.',
  },
  {
    step: '07',
    titleKey: 'for_institutions.step7_title',
    title: 'Publish only when authorized',
    descKey: 'for_institutions.step7_desc',
    desc: "Create a public Trust Page only under the record's visibility and consent policy.",
  },
  {
    step: '08',
    titleKey: 'for_institutions.step8_title',
    title: 'Maintain status and audit history',
    descKey: 'for_institutions.step8_desc',
    desc: 'Preserve review, correction, suspension, revocation, and expiration events over time.',
  },
];

export default function ForInstitutionsContent() {
  const { t, safeT } = useTranslation();
  const footerChips = [
    { labelKey: 'nav.trust_ledger', label: 'Trust Ledger', href: '/trust-ledger' },
    {
      labelKey: 'for_institutions.chip_product_overview',
      label: 'Product overview',
      href: '/product',
    },
    { labelKey: 'for_institutions.chip_about', label: 'About Ertzyx', href: '/about' },
    { labelKey: 'for_institutions.chip_verify', label: 'Verify a record', href: '/verify' },
  ];

  return (
    <main className="public-page public-family-institution min-h-screen overflow-x-hidden">
      <LandingNav />

      <nav className="mx-auto max-w-5xl px-5 pb-0 pt-24">
        <ol className="flex items-center gap-2 text-xs text-slate-400">
          <li>
            <Link href="/" className="hover:text-slate-700">
              Ertzyx
            </Link>
          </li>
          <li>/</li>
          <li className="font-semibold text-slate-600">
            {t('for_institutions.breadcrumb' as any)}
          </li>
        </ol>
      </nav>

      <section className="relative overflow-hidden px-5 pb-16 pt-10 text-center">
        <div
          className="public-atmosphere absolute inset-0"
          style={{
            transform: 'scale(1.08)',
          }}
        />
        <div className="relative mx-auto max-w-4xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-black text-[var(--public-emerald)]">
            <Building2 size={13} />
            {t('for_institutions.badge' as any)}
          </div>
          <h1 className="public-heading text-4xl leading-tight sm:text-5xl lg:text-6xl">
            {safeT('for_institutions.hero_title_1' as any, 'Preserve institutional history.')}{' '}
            <span className="inline-block text-emerald-900">
              {safeT('for_institutions.hero_title_2' as any, 'Recognize human achievement.')}
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg font-medium leading-8 text-slate-700">
            {safeT(
              'for_institutions.hero_subtitle' as any,
              'Create programs, review evidence, issue trusted records, preserve organizational memory, and publish verification pages designed for long-term human context.'
            )}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link
              href="/register?role=institution"
              className="public-button public-button-primary px-6"
            >
              <Building2 size={16} />
              {safeT('for_institutions.request_access' as any, 'Create an institution workspace')}
            </Link>
            <Link
              href="/contact?topic=institution-consultation"
              className="public-button public-button-secondary px-6"
            >
              <MessageSquareText size={16} />
              {safeT('for_institutions.book_consultation' as any, 'Book a consultation')}
            </Link>
          </div>
          <p className="mx-auto mt-5 max-w-3xl text-sm font-semibold text-slate-600">
            For schools, employers, associations, archives, cultural organizations, nonprofits, and
            public programs.
          </p>
        </div>
      </section>

      <section className="px-5 py-16" style={{ background: '#eef6f0' }}>
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm font-black uppercase tracking-wider text-emerald-800">
            {t('for_institutions.what_it_is_eyebrow' as any)}
          </p>
          <h2 className="mt-2 text-3xl font-black text-slate-950">
            {t('for_institutions.what_it_is_title' as any)}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
            {t('for_institutions.what_it_is_body' as any)}
          </p>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-[#f7f4ee] px-5 py-16">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 max-w-3xl">
            <p className="text-sm font-black uppercase tracking-wider text-emerald-800">
              Two connected ledgers
            </p>
            <h2 className="mt-2 font-serif text-3xl text-slate-950 sm:text-4xl">
              Preserve human context. Publish accountable recognition.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <article className="rounded-[14px] border border-slate-300 bg-white p-7 shadow-sm">
              <Archive className="text-emerald-800" aria-hidden="true" />
              <h3 className="mt-5 font-serif text-2xl text-slate-950">Preservation Ledger</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                Curate institutional history, oral histories, photographs, milestones, program
                memory, community stories, and cultural heritage. Collections remain private unless
                an authorized curator publishes them.
              </p>
            </article>
            <article className="rounded-[14px] border border-slate-300 bg-white p-7 shadow-sm">
              <ShieldCheck className="text-[var(--public-sapphire)]" aria-hidden="true" />
              <h3 className="mt-5 font-serif text-2xl text-slate-950">Trust Ledger</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">
                Document participation, contribution, completion, awards, service, projects, and
                milestones with evidence, optional witness context, institutional attestation,
                status, and audit history.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-16">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <p className="text-sm font-black uppercase tracking-wider text-emerald-800">
              {t('for_institutions.what_you_get_eyebrow' as any)}
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-950">
              {t('for_institutions.what_you_get_title' as any)}
            </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ icon: Icon, title, titleKey, desc, descKey, color }) => (
              <div
                key={title}
                className="rounded-[14px] border border-slate-200 bg-slate-50/80 p-6"
              >
                <div
                  className={`mb-4 flex h-11 w-11 items-center justify-center rounded-[9px] ${color}`}
                >
                  <Icon size={20} />
                </div>
                <h3 className="text-base font-black text-slate-950">
                  {t(titleKey as any) || title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {t(descKey as any) || desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-16" style={{ background: '#eef4f8' }}>
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <p className="text-sm font-black uppercase tracking-wider text-emerald-800">
              {t('for_institutions.how_it_works_eyebrow' as any)}
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-950">
              {t('for_institutions.how_it_works_title' as any)}
            </h2>
          </div>
          <div className="space-y-4">
            {steps.map(({ step, title, titleKey, desc, descKey }) => (
              <div
                key={step}
                className="flex gap-5 rounded-[14px] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <div className="shrink-0 text-xl font-black text-slate-300">{step}</div>
                <div>
                  <h3 className="text-sm font-black text-slate-950">
                    {t(titleKey as any) || title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                    {t(descKey as any) || desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-5 py-16">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <p className="text-sm font-black uppercase tracking-wider text-emerald-800">
              {t('for_institutions.who_its_for_eyebrow' as any)}
            </p>
            <h2 className="mt-2 text-3xl font-black text-slate-950">
              {t('for_institutions.who_its_for_title' as any)}
            </h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map(({ icon: Icon, title, titleKey, desc, descKey }) => (
              <div
                key={title}
                className="rounded-[14px] border border-slate-200 bg-slate-50/80 p-5"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[var(--public-sapphire)]">
                  <Icon size={18} />
                </div>
                <h3 className="text-sm font-black text-slate-950">{t(titleKey as any) || title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">
                  {t(descKey as any) || desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 text-center" style={{ background: '#eef4f8' }}>
        <div className="mx-auto max-w-2xl">
          <h2 className="text-3xl font-black text-slate-950 sm:text-4xl">
            {t('for_institutions.cta_title' as any)}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            {t('for_institutions.cta_subtitle' as any)}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              href="/register?role=institution"
              className="inline-flex items-center gap-2 rounded-[9px] bg-[#0f6e56] px-6 py-3 text-sm font-black text-white shadow-sm transition-transform duration-150 ease-out hover:bg-[#1d9e75] active:scale-[0.97]"
            >
              <Building2 size={16} />
              {safeT('for_institutions.request_access' as any, 'Create an institution workspace')}
            </Link>
            <Link
              href="/contact?topic=institution-consultation"
              className="public-button public-button-trust px-6"
            >
              <MessageSquareText size={16} />
              {safeT('for_institutions.book_consultation' as any, 'Book a consultation')}
            </Link>
          </div>
          <p className="mt-5 text-sm text-slate-600">
            Already have a workspace?{' '}
            <Link
              href="/login?next=/institution-dashboard"
              className="font-bold text-[var(--public-sapphire)] underline underline-offset-4"
            >
              Institution sign in
            </Link>
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {footerChips.map(({ label, labelKey, href }) => (
              <Link
                key={label}
                href={href}
                className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-600 transition hover:text-slate-950"
              >
                {t(labelKey as any) || label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />
    </main>
  );
}
